import { useMemo } from 'react';
import { Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Boxes,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  Package,
  ShoppingBag,
  Stethoscope,
  Wallet,
} from 'lucide-react-native';

import { Badge, Card, EmptyState, Skeleton, StatTile } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { productosVentaQuery, insumosInternosQuery } from '@/services/firebase/inventario';
import { todosMovimientosQuery } from '@/services/firebase/contabilidad';
import { todasSolicitudesQuery } from '@/services/firebase/solicitudes';
import { ventasPendientes } from '@/services/firebase/petshops';
import { useAuth, selectIsAdmin, selectIsDoctor, selectIsRecepcion } from '@/stores/authStore';
import { useNav } from '@/stores/navStore';
import { colors, radius, typography } from '@/theme/tokens';
import { diasHasta, formatBs, formatDate } from '@/utils/format';
import type { Bloque, MovimientoContable, Solicitud, EstadoSolicitud } from '@/types/domain';

const BLOQUE_INFO: Record<Bloque, { label: string; color: string }> = {
  clinica: { label: 'Clínica', color: colors.magenta[700] },
};

export function DashboardScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const isAdmin = useAuth(selectIsAdmin);
  const isDoctor = useAuth(selectIsDoctor);
  const isRecepcion = useAuth(selectIsRecepcion);
  const perfil = useAuth((s) => s.perfil);
  const navigate = useNav((s) => s.setCurrent);

  const ventas = useFirestoreCollection(productosVentaQuery);
  const insumos = useFirestoreCollection(insumosInternosQuery);
  const movimientos = useFirestoreCollection(isAdmin ? todosMovimientosQuery : null);
  const solicitudesAdmin = useFirestoreCollection(isAdmin ? todasSolicitudesQuery : null);
  const ventasDanna = useFirestoreCollection(isRecepcion ? ventasPendientes('danna') : null);
  const ventasBruno = useFirestoreCollection(isRecepcion ? ventasPendientes('bruno') : null);
  // Doctora: en el dashboard ya no mostramos lista de "mis solicitudes" porque ahora
  // son por área. Si quiere ver pedidos, va a la pantalla Solicitudes y elige área.
  const solicitudesDoctor = { data: [] as Solicitud[], loading: false };

  const balancePorBloque = useMemo(() => {
    const ahora = new Date();
    const init = (): { ingresos: number; egresos: number } => ({ ingresos: 0, egresos: 0 });
    const map: Record<Bloque, { ingresos: number; egresos: number }> = {
      clinica: init(),
    };
    movimientos.data.forEach((m) => {
      // Pendientes de pago no cuentan al gasto ejecutado
      if ((m.estadoPago ?? 'pagado') === 'pendiente') return;
      const f = m.fecha.toDate();
      if (f.getMonth() !== ahora.getMonth() || f.getFullYear() !== ahora.getFullYear()) return;
      const slot = map[m.bloque];
      if (!slot) return;
      if (m.tipo === 'ingreso') slot.ingresos += m.monto;
      else slot.egresos += m.monto;
    });
    return map;
  }, [movimientos.data]);

  const ultimosMovimientos = useMemo(() => {
    return (isAdmin ? movimientos.data : []).slice(0, 6);
  }, [movimientos.data, isAdmin]);

  const solicitudesDelUsuario = isDoctor ? solicitudesDoctor.data : solicitudesAdmin.data;
  const solicitudesPendientes = useMemo(
    () => (isAdmin ? solicitudesAdmin.data.filter((s) => s.estado === 'pendiente').length : 0),
    [isAdmin, solicitudesAdmin.data],
  );

  const alertas = useMemo(() => {
    const all: Array<{ id: string; titulo: string; detalle: string; severidad: 'warning' | 'danger'; kind: 'venc' | 'agotado' | 'bajo' }> = [];
    [...ventas.data, ...insumos.data].forEach((p) => {
      if (p.stock === 0) {
        all.push({ id: `agotado-${p.id}`, titulo: `${p.nombre} agotado`, detalle: p.presentacion, severidad: 'danger', kind: 'agotado' });
      } else if (p.stockBajo) {
        all.push({ id: `bajo-${p.id}`, titulo: `${p.nombre} con stock bajo`, detalle: `Quedan ${p.stock} · mínimo ${p.stockMin}`, severidad: 'warning', kind: 'bajo' });
      }
      const d = diasHasta(p.vencimiento?.toDate?.() ?? null);
      if (d !== null && d <= 60) {
        all.push({ id: `venc-${p.id}`, titulo: `${p.nombre} ${d < 0 ? 'vencido' : `vence en ${d}d`}`, detalle: p.presentacion, severidad: d < 0 || d <= 30 ? 'danger' : 'warning', kind: 'venc' });
      }
    });
    return all.slice(0, 8);
  }, [ventas.data, insumos.data]);

  const balanceClinicaMes = balancePorBloque.clinica.ingresos - balancePorBloque.clinica.egresos;
  const gastoClinicaMes = balancePorBloque.clinica.egresos;
  const totalPetshopsPendientes = useMemo(() => {
    if (!isRecepcion) return { cantidad: 0, total: 0 };
    const all = [...ventasDanna.data, ...ventasBruno.data];
    return { cantidad: all.length, total: all.reduce((sum, v) => sum + (v.monto ?? 0), 0) };
  }, [isRecepcion, ventasDanna.data, ventasBruno.data]);

  const primerNombre = (perfil?.nombre ?? '').trim().split(/\s+/)[0] || '';
  const rolLabel = isAdmin ? 'Administración' : isDoctor ? 'Doctora' : isRecepcion ? 'Recepción' : '';
  const HOY = new Date();
  const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const fechaTxt = `${DIAS[HOY.getDay()]} ${HOY.getDate()} de ${MESES[HOY.getMonth()]}`;

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: wide ? 20 : 16, paddingBottom: 80 }}>
      {/* Saludo */}
      <View style={{ gap: 2 }}>
        <Text style={{ fontFamily: typography.display.fontFamily, fontSize: wide ? 26 : 22, fontWeight: typography.weights.bold, color: colors.ink[900], letterSpacing: -0.4 }}>
          {primerNombre ? `Hola, ${primerNombre} 👋` : 'Hola 👋'}
        </Text>
        <Text style={{ fontSize: 13, color: colors.ink[500] }}>
          {rolLabel ? `${rolLabel} · ` : ''}{fechaTxt}
        </Text>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: wide ? 12 : 10 }}>
        <StatTile
          label="Farmacia"
          value={ventas.loading ? '—' : String(ventas.data.length)}
          accent="magenta"
          icon={<Boxes size={22} color={colors.magenta[700]} />}
          hint={`${ventas.data.filter((p) => p.stockBajo).length} bajo stock`}
          onPress={() => navigate('ProductosVenta')}
        />
        {isAdmin || isDoctor ? (
          <StatTile
            label="Insumos internos"
            value={insumos.loading ? '—' : String(insumos.data.length)}
            accent="magenta"
            icon={<Stethoscope size={22} color={colors.magenta[700]} />}
            hint={`${insumos.data.filter((p) => p.stockBajo).length} bajo stock`}
            onPress={() => navigate('InsumosInternos')}
          />
        ) : null}
        <StatTile
          label="Alertas activas"
          value={String(alertas.length)}
          accent={alertas.length > 0 ? 'warning' : 'success'}
          icon={<AlertTriangle size={22} color={alertas.length > 0 ? colors.warning[600] : colors.success[600]} />}
          onPress={() => navigate('Alertas')}
        />
        {isAdmin ? (
          <StatTile
            label="Gasto del mes"
            value={formatBs(gastoClinicaMes)}
            accent="danger"
            icon={<Wallet size={22} color={colors.danger[600]} />}
            hint="Acumulado clínica"
            onPress={() => navigate('Contabilidad')}
          />
        ) : null}
        {isDoctor ? (
          <StatTile
            label="Solicitudes pendientes"
            value={String(solicitudesPendientes)}
            accent="magenta"
            icon={<ClipboardList size={22} color={colors.magenta[700]} />}
            onPress={() => navigate('Solicitudes')}
          />
        ) : null}
        {isRecepcion ? (
          <StatTile
            label="Petshops sin rendir"
            value={String(totalPetshopsPendientes.cantidad)}
            accent="magenta"
            icon={<ShoppingBag size={22} color={colors.magenta[700]} />}
            hint={totalPetshopsPendientes.cantidad > 0 ? formatBs(totalPetshopsPendientes.total) : 'Todo al día'}
            onPress={() => navigate('Petshops')}
          />
        ) : null}
      </View>

      <View style={{ flexDirection: wide ? 'row' : 'column', gap: 16 }}>
        <Card style={{ flex: 1.4, gap: 14 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Alertas activas
            </Text>
            <Badge label={`${alertas.length}`} tone={alertas.length === 0 ? 'success' : 'warning'} />
          </View>

          {ventas.loading || insumos.loading ? (
            <View style={{ gap: 10 }}>
              <Skeleton height={48} />
              <Skeleton height={48} />
              <Skeleton height={48} />
            </View>
          ) : alertas.length === 0 ? (
            <EmptyState illustration="cat" title="Todo ronroneando" message="No hay alertas. Tu inventario está al día." />
          ) : (
            <View style={{ gap: 8 }}>
              {alertas.map((a) => {
                const Icon = a.kind === 'venc' ? CalendarClock : a.kind === 'agotado' ? Package : AlertTriangle;
                const tone = a.severidad === 'danger' ? colors.danger : colors.warning;
                return (
                  <Pressable
                    key={a.id}
                    onPress={() => navigate('Alertas')}
                    style={({ hovered }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      padding: 12,
                      borderRadius: radius.xl,
                      backgroundColor: hovered ? tone[50] : 'transparent',
                      borderWidth: 1,
                      borderColor: hovered ? tone[500] : colors.ink[100],
                      // @ts-ignore web only
                      cursor: 'pointer',
                      // @ts-ignore web only
                      transitionProperty: 'background-color, border-color',
                      // @ts-ignore web only
                      transitionDuration: '160ms',
                    })}
                  >
                    <Icon size={20} color={tone[600]} />
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 14 }}>{a.titulo}</Text>
                      <Text style={{ color: colors.ink[500], fontSize: 12, marginTop: 2 }}>{a.detalle}</Text>
                    </View>
                    <ChevronRight size={16} color={colors.ink[400]} />
                  </Pressable>
                );
              })}
            </View>
          )}
        </Card>

        {isAdmin ? (
          <Card style={{ flex: 1.6, gap: 14 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                Gastos del mes
              </Text>
              <Pressable
                onPress={() => navigate('Contabilidad')}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 10,
                  height: 28,
                  borderRadius: radius.full,
                  backgroundColor: hovered ? colors.magenta[100] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ color: colors.magenta[700], fontSize: 12, fontWeight: typography.weights.bold }}>
                  Ver todos
                </Text>
                <ChevronRight size={14} color={colors.magenta[700]} />
              </Pressable>
            </View>

            <View
              style={{
                padding: 14,
                borderRadius: radius.xl,
                backgroundColor: colors.crema[50],
                borderLeftWidth: 3,
                borderLeftColor: colors.magenta[700],
                gap: 4,
              }}
            >
              <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Clínica · gastos acumulados
              </Text>
              <Text
                style={{
                  fontFamily: typography.display.fontFamily,
                  fontSize: 22,
                  fontWeight: typography.weights.bold,
                  color: colors.danger[600],
                }}
                numberOfLines={1}
              >
                {formatBs(gastoClinicaMes)}
              </Text>
            </View>

            <View style={{ height: 1, backgroundColor: colors.ink[100] }} />

            <Text style={{ fontSize: 12, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Últimos movimientos
            </Text>

            {movimientos.loading ? (
              <View style={{ gap: 8 }}>
                <Skeleton height={48} />
                <Skeleton height={48} />
                <Skeleton height={48} />
              </View>
            ) : ultimosMovimientos.length === 0 ? (
              <View style={{ paddingVertical: 24, alignItems: 'center' }}>
                <Text style={{ color: colors.ink[500], fontSize: 13 }}>Aún no hay movimientos registrados.</Text>
              </View>
            ) : (
              <View style={{ gap: 6 }}>
                {ultimosMovimientos.map((m) => (
                  <MovimientoMini key={m.id} mov={m} onPress={() => navigate('Contabilidad')} />
                ))}
              </View>
            )}
          </Card>
        ) : null}

        {isDoctor ? (
          <Card style={{ flex: 1.4, gap: 14 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                Mis solicitudes
              </Text>
              <Pressable
                onPress={() => navigate('Solicitudes')}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 10,
                  height: 28,
                  borderRadius: radius.full,
                  backgroundColor: hovered ? colors.magenta[100] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ color: colors.magenta[700], fontSize: 12, fontWeight: typography.weights.bold }}>
                  Ver todas
                </Text>
                <ChevronRight size={14} color={colors.magenta[700]} />
              </Pressable>
            </View>
            {solicitudesDoctor.loading ? (
              <View style={{ gap: 8 }}>
                <Skeleton height={56} />
                <Skeleton height={56} />
              </View>
            ) : solicitudesDelUsuario.length === 0 ? (
              <EmptyState
                illustration="note"
                title="No has pedido material"
                message="Tocá «Pedir material» en el menú para crear tu primera solicitud."
              />
            ) : (
              <View style={{ gap: 6 }}>
                {solicitudesDelUsuario.slice(0, 5).map((s) => (
                  <SolicitudMini key={s.id} solicitud={s} onPress={() => navigate('Solicitudes')} />
                ))}
              </View>
            )}
          </Card>
        ) : null}
      </View>
    </ScrollView>
  );
}

function MovimientoMini({ mov, onPress }: { mov: MovimientoContable; onPress: () => void }) {
  const Icon = mov.tipo === 'ingreso' ? ArrowDownRight : ArrowUpRight;
  const tone = mov.tipo === 'ingreso' ? colors.success : colors.danger;
  const info = BLOQUE_INFO[mov.bloque] ?? { label: mov.bloque ?? '—', color: colors.ink[400] };
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        borderRadius: radius.lg,
        backgroundColor: hovered ? colors.crema[50] : 'transparent',
        // @ts-ignore web only
        cursor: 'pointer',
        // @ts-ignore web only
        transitionProperty: 'background-color',
        // @ts-ignore web only
        transitionDuration: '120ms',
      })}
    >
      <Icon size={16} color={tone[600]} />
      <View style={{ flex: 1 }}>
        <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 13 }} numberOfLines={1}>
          {mov.descripcion}
        </Text>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: info.color }} />
            <Text style={{ fontSize: 11, color: colors.ink[500] }}>{info.label}</Text>
          </View>
          <Text style={{ fontSize: 11, color: colors.ink[400] }}>·</Text>
          <Text style={{ fontSize: 11, color: colors.ink[500] }}>{formatDate(mov.fecha.toDate())}</Text>
        </View>
      </View>
      <Text
        style={{
          fontFamily: typography.display.fontFamily,
          fontSize: 14,
          fontWeight: typography.weights.bold,
          color: tone[600],
        }}
      >
        {mov.tipo === 'ingreso' ? '+' : '−'} {formatBs(mov.monto)}
      </Text>
    </Pressable>
  );
}

const ESTADO_TONO_MINI: Record<EstadoSolicitud, 'magenta' | 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  pendiente: 'warning',
  aprobada: 'info',
  comprada: 'magenta',
  entregada: 'success',
  cancelada: 'neutral',
  rechazada: 'danger',
};

function SolicitudMini({ solicitud, onPress }: { solicitud: Solicitud; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        borderRadius: radius.lg,
        backgroundColor: hovered ? colors.crema[50] : 'transparent',
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      <ClipboardList size={16} color={colors.magenta[700]} />
      <View style={{ flex: 1 }}>
        <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 13 }} numberOfLines={1}>
          {solicitud.items.length} insumo{solicitud.items.length === 1 ? '' : 's'}
        </Text>
        <Text style={{ fontSize: 11, color: colors.ink[500], marginTop: 2 }}>
          {formatDate(solicitud.creadaEn?.toDate?.() ?? null)}
        </Text>
      </View>
      <Badge label={solicitud.estado.toUpperCase()} tone={ESTADO_TONO_MINI[solicitud.estado]} />
    </Pressable>
  );
}
